import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button } from '@mui/material'
import React, { useCallback } from 'react'
import useModals from 'src/Hooks/useModals'
import { CompanyUserData } from 'src/State/User/UserModel'
import { CompanyInvitationModel } from '../models/company.model'
import { useTypedSelector } from 'src/State/RootReducer'
import { CompanySettingsInviteActionMenu, CompanySettingsUserActionMenu, CompanyInstallerTableItemModel } from 'src/Libs/company-settings'
import { Table } from '@e3dc-react/shell/Elements/Table'
import { J2Add } from '@e3dc-react/icons'
import useCompanyInstallerTableActions from '../hooks/use-company-installers-actions'
import useCompanyUserTable from '../hooks/use-company-user-table'
interface CompanyInstallersProps {
  companyId: number
  installers: Array<CompanyInstallerTableItemModel>
  loading?: boolean
  onChangeNotifications: (value: boolean, companyId: number, userId: number, notificationId: number | null) => void
}

const CompanyInstallers: React.FC<CompanyInstallersProps> = ({ installers, loading = false, companyId, onChangeNotifications }) => {
  const { t } = useCustomTranslation()
  const { openInviteEditor } = useModals()
  const companyShopRights = useTypedSelector(store => store.user.company?.shopPermission)
  const companyManager = useTypedSelector(store => store.user.company?.Manager)
  const companyName = useTypedSelector(store => store.user.company?.Name)
  const { onChangeShop, onDeleteInstaller, onDeleteInvitation, onReSendInvite, onSendInvite, onChangeManager } =
    useCompanyInstallerTableActions(companyId)
  const handleChangeNotifications = useCallback(
    (value: boolean, companyId: number, userId: number, notificationId: number | null): void => {
      onChangeNotifications(value, companyId, userId, notificationId)
    },
    [onChangeNotifications]
  )

  const { header: tableHeader, data: tableData } = useCompanyUserTable(
    installers,
    companyId,
    Boolean(companyShopRights),
    onChangeShop,
    handleChangeNotifications,
    onReSendInvite
  )

  const menuOptions: ((item: CompanyInstallerTableItemModel) => React.ReactNode)[] = [
    item => {
      if (item.isInvite) {
        return (
          <CompanySettingsInviteActionMenu
            item={item.value as CompanyInvitationModel}
            onRemove={onDeleteInvitation}
            onResend={onReSendInvite}
            companyId={companyId}
          />
        )
      }
      if (companyManager === item.id) {
        return
      }
      return (
        <CompanySettingsUserActionMenu
          item={item.value as CompanyUserData}
          onRemove={onDeleteInstaller}
          onChangeManager={(user, changeManagerReason, removeOldManager) => onChangeManager(user, changeManagerReason, removeOldManager)}
          companyName={companyName || ''}
          managerId={companyManager}
        />
      )
    },
  ]

  const customFunctionalities = menuOptions.map(func => installers?.map(installer => func(installer)) ?? [])

  if (loading) {
    return <Loading />
  }

  return (
    <Box>
      <Table
        header={tableHeader}
        data={tableData}
        customFunctionalities={customFunctionalities}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
      />
      <Box sx={{ mt: 5 }}>
        <Button
          startIcon={<J2Add />}
          onClick={() =>
            openInviteEditor({
              onSave: onSendInvite,
              size: 'md',
              title: t('user.company.invitation.title'),
              description: t('user.company.invitation.description'),
            })
          }
        >
          {t('user.company.invitation.inviteUser')}
        </Button>
      </Box>
    </Box>
  )
}

export default CompanyInstallers
